/**
 * Focus area types — settings, workspace aggregate, notification prefs.
 *
 * 6 fixed buckets registered statically: FA1-4 (real focus areas), BO
 * (Business Ops / Meisterplan), and UN (Unclassified — demands not yet
 * routed to an FA). Only meeting fields are editable per FA via the
 * workspace settings panel. Must stay in sync with the backend
 * FOCUS_AREAS registry in routers/focus_areas.py.
 */

export interface FocusArea {
  code: string
  name: string
}

export const FOCUS_AREAS: FocusArea[] = [
  { code: 'FA1', name: 'Target ID & Validation' },
  { code: 'FA2', name: 'Target to IND' },
  { code: 'FA3', name: 'Clinical Dev & Regulatory' },
  { code: 'FA4', name: 'Foundational Models & Data' },
  { code: 'BO', name: 'Business Ops' },
  { code: 'UN', name: 'Unclassified' },
]

/**
 * A code with its name — "BO · Business Ops". Bare codes are council shorthand;
 * a requestor reading "BO" or "UN" on their own demand was never told what
 * either meant (#1377). Unknown codes pass through unchanged.
 */
export function focusAreaWithName(code: string | null | undefined): string {
  if (!code) return '—'
  const name = FOCUS_AREAS.find((f) => f.code === code)?.name
  return name ? `${code} · ${name}` : code
}

export interface FocusAreaSettings {
  focus_area_code: string
  meeting_cadence?: string | null
  meeting_topic?: string | null
  meeting_teams_link?: string | null
  next_meeting_at?: string | null
}

export interface FocusAreaSettingsUpdate {
  meeting_cadence?: string
  meeting_topic?: string
  meeting_teams_link?: string
  next_meeting_at?: string
}

// ── Per-FA custom tags ───────────────────────────────────────────────────────

export interface FaTag {
  id: number
  focus_area_code: string
  slug: string
  label: string
  active: boolean
}

export interface FaTagCreate {
  label: string
  slug?: string
}

export interface FaTagUpdate {
  label?: string
  active?: boolean
}

export interface FocusAreaTeamMember {
  id: number
  name: string
  email: string
  role: string
  is_you: boolean
}

export type WorkspaceTimeRange = 'quarter' | 'year' | 'all'

export interface WorkspaceHero {
  /** Active demands (scoping onward) with no caretaker at all (#1208). */
  unshepherded?: number
  overdue: number
  pending: number
  posted_no_apps: number
  stale_14d: number
}

export interface WorkspaceDigestItem {
  key: string
  count: number
  label: string
  tone: 'info' | 'success' | 'warning' | 'danger'
}

export interface WorkspacePulseCell {
  key: string
  label: string
  count: number
  sla_breaches: number
}

export interface WorkspaceBlocker {
  id: number
  demand_id: string
  title: string
  days_waiting: number
}

export interface WorkspaceThroughputTile {
  label: string
  value: string
  delta: string
  trend: 'good' | 'flat' | 'bad'
  _placeholder?: boolean
}

export interface WorkspaceDecision {
  id: number
  demand_id: string
  title: string
  action: string
  by_initials: string
  by_name?: string | null
  when?: string | null
}

/**
 * Bucket Mix — priority tier breakdown of an FA's active demands.
 *
 * Keys are the 5 quadrant slugs from the backend
 * ``services.priority_tier.VALID_PRIORITY_TIERS``. Values are counts.
 * Backend may omit this field for older deployments; the UI must treat
 * missing/empty as "render nothing".
 */
export type BucketMixKey =
  | 'major-unlocks'
  | 'high-priority'
  | 'quick-wins'
  | 'parking-lot'
  | 'rejected'

export type BucketMix = Partial<Record<BucketMixKey, number>>

export interface FocusAreaWorkspaceData {
  fa: { code: string; name: string; demand_count: number; talent_count: number }
  time_range: WorkspaceTimeRange
  hero: WorkspaceHero
  digest: WorkspaceDigestItem[]
  pulse: WorkspacePulseCell[]
  blockers: WorkspaceBlocker[]
  throughput: WorkspaceThroughputTile[]
  recent_decisions: WorkspaceDecision[]
  team: FocusAreaTeamMember[]
  /** Optional — present when backend supports issue #710 bucket mix. */
  bucket_mix?: BucketMix
}

export interface FANotifPrefRow {
  event_key: string
  label: string
  description?: string | null
  email_enabled: boolean
  in_app_enabled: boolean
}

export interface FANotifPrefsResponse {
  focus_area_code: string
  items: FANotifPrefRow[]
}

export interface FANotifPrefUpdate {
  email_enabled?: boolean
  in_app_enabled?: boolean
}
